import { useState } from 'react';
import { CompetitionEntryClient, CompetitionEntryDTO, ApiResponse_string, SearchRequestDTO_CompetitionEntrySearchRequestDTO } from '../services/apiClient';
import { API_BASE_URL } from '../config/config';
import axiosInstance from '../services/axiosInstance';

export const useCompetitionEntryApi = () => {
    const apiClient = new CompetitionEntryClient(API_BASE_URL, axiosInstance);

    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const create = async (data: CompetitionEntryDTO) => {
        setIsLoading(true);
        try {
            await apiClient.createCompetitionEntry(data);
        } catch (err: any) {
            setError(err.message || 'Error creating CompetitionEntry');
        } finally {
            setIsLoading(false);
        }
    };

    const edit = async (id: string, data: CompetitionEntryDTO) => {
        setIsLoading(true);
        try {
            await apiClient.updateCompetitionEntry(id, data);
        } catch (err: any) {
            setError(err.message || 'Error updating CompetitionEntry');
        } finally {
            setIsLoading(false);
        }
    };

    const getById = async (id: string) => {
        setIsLoading(true);
        try {
            return await apiClient.getCompetitionEntryById(id);
        } catch (err: any) {
            setError(err.message || 'Error fetching CompetitionEntry');
        } finally {
            setIsLoading(false);
        }
    };

    const search = async (filters: any) => {
        setIsLoading(true);
        try {
            return await apiClient.searchCompetitionEntries(filters);
        } catch (err: any) {
            setError(err.message || 'Error searching CompetitionEntries');
        } finally {
            setIsLoading(false);
        }
    };

    const updateProofOfPayment = async (id: string, fileRecordId: string) => {
        setIsLoading(true);
        try {
            await apiClient.updateCompetitionEntryProofOfPayment(id, fileRecordId);
        } catch (err: any) {
            setError(err.message || 'Error updating proof of payment');
            throw err;
        } finally {
            setIsLoading(false);
        }
    };

    const updateStatus = async (recordIds: string[], competitionStatusId: string) => {
        setIsLoading(true);
        try {
            await apiClient.updateCompetitionEntryStatus({ recordIds, competitionStatusId });
        } catch (err: any) {
            setError(err.message || 'Error updating CompetitionEntry status');
            throw err;
        } finally {
            setIsLoading(false);
        }
    };

    const remove = async (id: string) => {
        setIsLoading(true);
        try {
            await apiClient.deleteCompetitionEntry(id);
        } catch (err: any) {
            setError(err.message || 'Error deleting CompetitionEntry');
        } finally {
            setIsLoading(false);
        }
    };

    const extract = async (
        filters: any
    ): Promise<ApiResponse_string> => {
        setIsLoading(true);
        try {
            return await apiClient.exportCompetitionEntries(filters);
        } catch (err: unknown) {
            handleHookError(err, 'Error exporting CompetitionEntries', setError);
            throw err;
        } finally {
            setIsLoading(false);
        }
    };

    const getDistinctYears = async (): Promise<number[]> => {
        setIsLoading(true);
        try {
            const response = await apiClient.getCompetitionEntryDistinctYears();
            // NSwag collapses distinct ApiResponse<IEnumerable<T>> shapes into one generated
            // interface, so the generated type here is wrong (mirrors the same cast used by
            // useLightweightCompetitionResultEntries) - the actual runtime payload is number[].
            return (response?.data as unknown as number[]) || [];
        } catch (err: any) {
            setError(err.message || 'Error fetching CompetitionEntry years');
            return [];
        } finally {
            setIsLoading(false);
        }
    };

    const generateParticipationCertificate = async (memberId: string, year: number) => {
        setIsLoading(true);
        try {
            const response = await apiClient.generateCompetitionParticipationCertificate(memberId, year);
            return response?.data;
        } catch (err: any) {
            setError(err.message || 'Error generating participation certificate');
            throw err;
        } finally {
            setIsLoading(false);
        }
    };

    const generateHtmlParticipationCertificate = async (memberId: string, year: number) => {
        setIsLoading(true);
        try {
            const response = await apiClient.generateHtmlCompetitionParticipationCertificate(memberId, year);
            return response?.data;
        } catch (err: any) {
            setError(err.message || 'Error generating HTML participation certificate');
            throw err;
        } finally {
            setIsLoading(false);
        }
    };

    return {
        create, edit, getById, search, extract, remove, updateProofOfPayment, updateStatus,
        getDistinctYears, generateParticipationCertificate, generateHtmlParticipationCertificate,
        isLoading, error,
    };
};